import { useEffect, useRef } from 'react';
import { useWebSocket } from '../../contexts/WebSocketContext';

/**
 * Call `reload` when a live event `matches`, bunched: several events inside
 * `waitMs` are one reload.
 *
 * FOR VIEWS THAT LOADED ONCE. A background job (a build, a session, a sync)
 * changed what the view showed while it sat on its first read, and only a
 * manual reload of the page showed the result.
 */
export function useLiveReload(matches: (event: any) => boolean, reload: () => void, waitMs = 2_000): void {
  const { subscribe } = useWebSocket();
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latest = useRef({ matches, reload });
  latest.current = { matches, reload };
  useEffect(() => {
    const off = subscribe((event: any) => {
      if (!latest.current.matches(event) || timer.current) return;
      timer.current = setTimeout(() => {
        timer.current = null;
        latest.current.reload();
      }, waitMs);
    });
    return () => {
      off?.();
      if (timer.current) clearTimeout(timer.current);
      timer.current = null;
    };
  }, [subscribe, waitMs]);
}

/**
 * Call `reload` every `everyMs` while `running` is true. The read that shows
 * the work finished is the one that turns `running` off, so nothing more is
 * needed at the end.
 *
 * FOR WORK THAT SENDS NO LIVE EVENT (deploys, a Search Console pull): look
 * again while it is going, and stop when it is done.
 */
export function usePollWhile(running: boolean, reload: () => void, everyMs = 5_000): void {
  const latest = useRef(reload);
  latest.current = reload;
  useEffect(() => {
    if (!running) return;
    const t = window.setInterval(() => latest.current(), everyMs);
    return () => window.clearInterval(t);
  }, [running, everyMs]);
}
